import {
	FileSpreadsheet,
	Filter,
	AlertTriangle,
	ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
	SearchableSelect,
	type SearchableSelectOption,
} from "@/components/ui/searchable-select";
import { MultiSelect } from "@/components/ui/multi-select";
import SkeletonLoader from "@/components/ui/skeleton-loader";
import {
	useProdReports,
	type UseProdReportsOptions,
} from "./useProdReports";
import { exportProdReportXls } from "./exportProdReportXls";
import ProdReportTable from "./ProdReportTable";

function buildStandaloneUrl(
	sprintId: string,
	projectIds: string[],
	userIds: string[],
): string {
	const params = new URLSearchParams();
	if (sprintId) params.set("sprintId", sprintId);
	projectIds.forEach((id) => params.append("projectId", id));
	userIds.forEach((id) => params.append("userId", id));
	const qs = params.toString();
	return `/admin/prod-reports${qs ? `?${qs}` : ""}`;
}

export default function ProdReportsCard({
	options,
	standalone = false,
}: {
	options?: UseProdReportsOptions;
	standalone?: boolean;
}) {
	const {
		projects,
		sprints,
		users,
		refLoading,
		refError,
		projectIds,
		sprintId,
		userIds,
		setProjectIds,
		setSprintId,
		setUserIds,
		groups,
		sprint,
		loading,
		error,
		applied,
		apply,
	} = useProdReports(options);

	const projectOptions: SearchableSelectOption[] = projects.map((p) => ({
		value: p.id,
		label: p.name,
	}));
	const sprintOptions: SearchableSelectOption[] = sprints.map((s) => ({
		value: s.id,
		label: s.name,
	}));
	const userOptions: SearchableSelectOption[] = users.map((u) => ({
		value: u.id,
		label: u.full_name ?? u.email,
		description: u.full_name ? u.email : undefined,
	}));

	const hasData = applied && groups.length > 0;

	return (
		<Card className="my-6 overflow-hidden p-0">
			{/* Header */}
			<div className="flex flex-col items-start justify-between gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center">
				<div>
					<h2 className="text-base font-semibold text-foreground">
						Prod Reports
					</h2>
					<p className="mt-0.5 text-xs text-muted">
						Tasks grouped by user for the selected sprint.
					</p>
				</div>
				<div className="flex items-center gap-2">
					<Button
						variant="outline"
						size="sm"
						className="gap-2"
						disabled={!hasData}
						onClick={() => exportProdReportXls(groups, sprint)}
					>
						<FileSpreadsheet className="h-3.5 w-3.5" />
						Export to Excel
					</Button>
					{!standalone && (
						<Button
							variant="outline"
							size="sm"
							className="gap-2"
							onClick={() =>
								window.open(
									buildStandaloneUrl(
										sprintId,
										projectIds,
										userIds,
									),
									"_blank",
									"noopener,noreferrer",
								)
							}
						>
							<ExternalLink className="h-3.5 w-3.5" />
							Open in new tab
						</Button>
					)}
				</div>
			</div>

			{/* Filters */}
			<div className="border-b border-border px-5 py-4">
				{refError ? (
					<p className="text-sm text-danger">{refError}</p>
				) : (
					<div className="flex flex-col gap-3 lg:flex-row lg:items-end">
						<div className="flex-1">
							<label className="mb-1 block text-xs text-muted">
								Project
							</label>
							<MultiSelect
								value={projectIds}
								onValueChange={setProjectIds}
								options={projectOptions}
								placeholder="All projects"
								disabled={refLoading}
								ariaLabel="Filter by project"
							/>
						</div>
						<div className="flex-1">
							<label className="mb-1 block text-xs text-muted">
								Sprint
							</label>
							<SearchableSelect
								value={sprintId}
								onValueChange={setSprintId}
								options={sprintOptions}
								placeholder="All sprints"
								clearable
								disabled={refLoading}
								ariaLabel="Filter by sprint"
							/>
						</div>
						<div className="flex-1">
							<label className="mb-1 block text-xs text-muted">
								Users
							</label>
							<MultiSelect
								value={userIds}
								onValueChange={setUserIds}
								options={userOptions}
								placeholder="All users"
								disabled={refLoading}
								ariaLabel="Filter by user"
							/>
						</div>
						<Button
							className="gap-2"
							disabled={refLoading || loading}
							onClick={apply}
						>
							<Filter className="h-4 w-4" />
							Apply Filters
						</Button>
					</div>
				)}
			</div>

			{/* Results */}
			<div className="px-5 py-4">
				{loading ? (
					<div className="space-y-3">
						<SkeletonLoader className="h-6 w-32" />
						<SkeletonLoader className="h-24 w-full" />
					</div>
				) : error ? (
					<div className="flex items-start gap-2 text-danger">
						<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
						<p className="text-sm">{error}</p>
					</div>
				) : !applied ? (
					<p className="py-8 text-center text-sm text-muted">
						Apply filters to see results.
					</p>
				) : groups.length === 0 ? (
					<p className="py-8 text-center text-sm text-muted">
						No tasks match the selected filters.
					</p>
				) : (
					<ProdReportTable groups={groups} sprint={sprint} />
				)}
			</div>
		</Card>
	);
}
